import { useId } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '@/shared/lib/use-focus-trap';
import { usePortalTarget } from '@/shared/ui/portal-host';

/**
 * Minimal accessible modal dialog (no extra dependency). Renders a token-styled card over a scrim,
 * labelled by its title, closes on Escape and scrim click. On open it moves focus into the panel
 * (first focusable control, else the panel itself) and TRAPS Tab/Shift+Tab within it; on close it
 * restores focus to the element that was focused before opening. Reduced-motion users get no entrance
 * animation.
 *
 * SMALL MODALS ONLY (ADR-0033). After the editing-surface rework the only sanctioned uses are
 * {@link ConfirmDialog} (destructive confirms + the edit-route unsaved guard) and tiny non-form
 * "preview" modals (quest-archive preview, photo-proof viewer). Content FORMS belong on full edit
 * routes ({@link EditPage}); short scoped actions belong in an {@link ActionSheet}. The panel is a
 * fixed small width — there is intentionally no `size` prop, so the type system blocks growing a form
 * back into a modal.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
}): React.ReactElement | null {
  const { panelRef, onKeyDown } = useFocusTrap(open, onClose);
  const titleId = useId();
  const descId = useId();
  // The persona shell's in-scope host (else `document.body`): escapes ancestor stacking/opacity/transform
  // contexts (a card at `opacity-70` must not bleed onto this fixed overlay) while still rendering inside
  // the app's theme scope, so the panel's tokens match the page rather than the OS colour scheme.
  const portalTarget = usePortalTarget();

  if (!open) return null;

  return createPortal(
    <div
      className="overlay-fade fixed inset-0 z-50 flex items-end justify-center bg-text/60 p-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        className="sheet-rise w-full max-w-sm rounded-card border border-border bg-surface p-5 shadow-lg outline-none"
      >
        <h2 id={titleId} className="font-display text-lg font-bold text-text">
          {title}
        </h2>
        {description && (
          <p id={descId} className="mt-1.5 text-sm text-text-muted">
            {description}
          </p>
        )}
        <div className="mt-4 flex flex-col gap-3">{children}</div>
      </div>
    </div>,
    portalTarget,
  );
}
